/**
 * ActiveFilters — EXL-672
 *
 * Horizontal chip bar displaying all active search filters.
 *
 * Chip format: "[Facet]: [Value]" with remove (×) button.
 * Chips appear in order: category → type → tags → status → date range.
 * Each chip is removed individually (no multi-select/shift-click behavior).
 *
 * "Clear all" button at the end of the chip list.
 */

import type { SearchFiltersState } from '../../hooks/useSearchFilters';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ActiveChip {
  key: string;
  label: string;
  onRemove: () => void;
}

interface ActiveFiltersProps {
  filters: SearchFiltersState;
  onCategoryRemove: (value: string) => void;
  onTypeRemove: () => void;
  onTagRemove: (value: string) => void;
  onStatusRemove: (value: string) => void;
  onDateRangeRemove: () => void;
  onClearAll: () => void;
}

// ─── Type label helper ────────────────────────────────────────────────────────

const TYPE_LABELS: Record<string, string> = {
  articles: 'Articles',
  kb: 'Knowledge Base',
  guides: 'Guides',
  faqs: 'FAQs',
};

function formatDateRange(from: string, to: string): string {
  const fmt = (iso: string) => {
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
  };
  if (from && to) return `${fmt(from)} – ${fmt(to)}`;
  if (from) return `After ${fmt(from)}`;
  if (to) return `Before ${fmt(to)}`;
  return '';
}

// ─── Chip component ───────────────────────────────────────────────────────────

interface ChipProps {
  label: string;
  onRemove: () => void;
}

function Chip({ label, onRemove }: ChipProps) {
  return (
    <span
      className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 text-xs font-medium
        bg-neutral-100 text-neutral-800 rounded-full border border-neutral-200
        hover:bg-neutral-200 transition-colors"
    >
      <span>{label}</span>
      <button
        type="button"
        onClick={onRemove}
        className="flex items-center justify-center w-4 h-4 rounded-full
          hover:bg-neutral-300 focus:outline-none focus:ring-1 focus:ring-neutral-900/30
          transition-colors"
        aria-label={`Remove filter: ${label}`}
      >
        <svg
          className="w-2.5 h-2.5 text-neutral-600"
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </span>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function ActiveFilters({
  filters,
  onCategoryRemove,
  onTypeRemove,
  onTagRemove,
  onStatusRemove,
  onDateRangeRemove,
  onClearAll,
}: ActiveFiltersProps) {
  // Build chips in order: category → type → tags → status → date range
  const chips: ActiveChip[] = [];

  for (const cat of filters.categories) {
    chips.push({
      key: `category:${cat}`,
      label: `Category: ${cat}`,
      onRemove: () => onCategoryRemove(cat),
    });
  }

  if (filters.type) {
    chips.push({
      key: `type:${filters.type}`,
      label: `Type: ${TYPE_LABELS[filters.type] ?? filters.type}`,
      onRemove: onTypeRemove,
    });
  }

  for (const tag of filters.tags) {
    chips.push({
      key: `tags:${tag}`,
      label: `Tags: ${tag}`,
      onRemove: () => onTagRemove(tag),
    });
  }

  for (const status of filters.statuses) {
    chips.push({
      key: `status:${status}`,
      label: `Status: ${status.charAt(0).toUpperCase() + status.slice(1)}`,
      onRemove: () => onStatusRemove(status),
    });
  }

  if (filters.dateFrom || filters.dateTo) {
    const rangeLabel = formatDateRange(filters.dateFrom, filters.dateTo);
    chips.push({
      key: 'date_range',
      label: `Date: ${rangeLabel}`,
      onRemove: onDateRangeRemove,
    });
  }

  if (chips.length === 0) return null;

  return (
    <div
      className="flex flex-wrap items-center gap-2 py-2"
      aria-label="Active filters"
      role="list"
    >
      {chips.map((chip) => (
        <div key={chip.key} role="listitem">
          <Chip label={chip.label} onRemove={chip.onRemove} />
        </div>
      ))}

      {chips.length > 1 && (
        <button
          type="button"
          onClick={onClearAll}
          className="text-xs font-medium text-neutral-500 hover:text-red-600
            transition-colors underline"
        >
          Clear all
        </button>
      )}
    </div>
  );
}

export default ActiveFilters;
